import React from 'react';
import {observer} from 'mobx-react';
import Toolbar from './Toolbar';
import PdfPreview from 'main/components/pdf/preview/components/PdfPreview';
import AnnotatablePdfPreviewState from 'main/components/pdf/annotate/state/AnnotatablePdfPreviewState';
import {loader} from 'shared/core';
import annotatablePdfStore from 'main/components/pdf/annotate/store/AnnotatablePdfStore';
import {ModalButtons} from 'shared/components/modals';
import {DocumentAssignmentRules} from 'main/components';
import FormError from 'shared/components/FormError';
/*global document*/

class AnnotatablePdfPreview extends React.Component {
  fabricCanvas;
  canvas;

  componentDidMount() {
    const {fabric} = this.props.uiState;
    // fabric-canvas is rendered inside <PdfPreview/>
    this.fabricCanvas = new fabric.Canvas('fabric-canvas', { allowTouchScrolling: true });
    this.fabricCanvas.on('selection:created', this.onSelectionCreated);
    this.fabricCanvas.on('selection:cleared', this.onSelectionCleared);
    this.fabricCanvas.on('mouse:up', this.onClick);
    this.fabricCanvas.renderOnAddRemove = false;
    this.props.uiState.registerCallback('onAnnotationsAdded', this.onAnnotationsAdded);
    this.props.uiState.registerCallback('onPageRefreshed', this.onPageRefreshed);
    this.props.uiState.registerCallback('rerenderFabricCanvas', this.rerenderFabricCanvas);

    document.addEventListener('keydown', this.onKeyDown);

    this.fabricCanvas.on('object:moving', this.onObjectMoving);
    this.fabricCanvas.on('object:scaling', this.onObjectScaling);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onClick = (e) => {
    if(!e.target) {
      this.props.uiState.selectedAnnotation = null;
    } else if (e.target.descriptor) {
      const matchers = [
        'input',
        'employee_signature',
        'issued_date',
        'signed_date',
        'assignee_name',
        'assignee_legal_name'
      ];
      const {annotationType, key} = e.target.descriptor;
      if ([annotationType, key].some(attr => matchers.includes(attr))) {
        if (e.target.canvas) {
          this.props.uiState.selectedAnnotation = e.target;
        }
      } else {
        this.props.uiState.selectedAnnotation = null;
      }
    }
  };

  onKeyDown = (e) => {
    if (e.target.closest('input, textarea, [contenteditable]')) return;

    switch (e.key) {
      case 'Backspace':
      case 'Delete':
        this.deleteSelectedAnnotations();
        break;
      default:
        break;
    }
  };

  deleteSelectedAnnotations = () => {
    const selectedAnnotations = this.fabricCanvas.getActiveObjects();
    if (!selectedAnnotations.length) return;

    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.remove(...selectedAnnotations);
    this.props.uiState.onAnnotationsDeleted(selectedAnnotations);
    this.fabricCanvas.renderAll();
  };

  // Objects inside a multi-selection are positioned relative to the selection, so it is
  // dissolved first to put them back in page coordinates.
  save = () => {
    this.fabricCanvas.discardActiveObject();
    return this.props.uiState.save();
  };

  rerenderFabricCanvas = () => {
    this.fabricCanvas.renderAll();
  };

  onSelectionCreated = (e) => {
    this.fabricCanvas.allowTouchScrolling = false;
    this.copyCoords(e.target, this.props.uiState.lastKnownBestCoords);
  };

  onSelectionCleared = () => {
    this.fabricCanvas.allowTouchScrolling = true;
  };

  onObjectMoving = (e) => {
    const obj = e.target;
    this.props.uiState.selectedAnnotation = null;

    if (obj.currentHeight > obj.canvas.height || obj.currentWidth > obj.canvas.width) {
      return;
    }

    obj.setCoords();
    const rect = obj.getBoundingRect();

    if (rect.top < 0) {
      obj.top = 0;
    } else if (rect.top + rect.height > obj.canvas.height) {
      obj.top = obj.canvas.height - rect.height;
    }

    if (rect.left < 0) {
      obj.left = 0;
    } else if (rect.left + rect.width > obj.canvas.width) {
      obj.left = obj.canvas.width - rect.width;
    }
  };

  onObjectScaling = (e) => {
    const obj = e.target;
    this.props.uiState.selectedAnnotation = null;
    obj.setCoords();
    const boundingRect = obj.getBoundingRect();

    if (this.objResizedOutOfBounds(obj, boundingRect)) {
      this.copyCoords(this.props.uiState.lastKnownBestCoords, obj);
    } else {
      this.copyCoords(obj, this.props.uiState.lastKnownBestCoords);
    }
  };

  copyCoords = (source, target) => {
    target.left = source.left;
    target.top = source.top;
    target.scaleX = source.scaleX;
    target.scaleY = source.scaleY;
    target.width = source.width;
    target.height = source.height;
  };

  objResizedOutOfBounds = (obj, boundingRect) => {
    const right = boundingRect.width + boundingRect.left;
    const bottom = boundingRect.height + boundingRect.top;

    return right >= obj.canvas.width ||
      bottom >= obj.canvas.height ||
      boundingRect.left < 0 ||
      boundingRect.top < 0;
  };

  onAnnotationsAdded = (annotations) => {
    for (const item of annotations) {
      item.objectCaching = false;
      this.fabricCanvas.add(item);
    }
    this.fabricCanvas.renderAll();
  };

  onPageChanging = () => {
    this.fabricCanvas.clear();
  };

  onPageChanged = (pageNumber) => {
    const {uiState} = this.props;

    this.fabricCanvas.setHeight(this.viewerElement.clientHeight);
    uiState.changePage(pageNumber, this.viewerElement.clientWidth, this.viewerElement.clientHeight);
  };

  onPageRefreshed = () => {
    setTimeout(() => this.fabricCanvas.renderAll(), 0);
  };

  onPdfLoaded = ({viewerElement}) => {
    this.viewerElement = viewerElement;
    this.fabricCanvas.setHeight(viewerElement.clientHeight);
    this.props.uiState.onLoaded();
  };

  render() {
    const {uiState, errors, employees, hideAssigneeSelector} = this.props;
    const {companyDocument, assigneeSelectorMenuPosition, selectedAnnotation, pdfUrl} = uiState;

    return (
      <div className='relative'>
        <Toolbar uiState={uiState}/>
        <PdfPreview
          employees={employees}
          hideAssigneeSelector={hideAssigneeSelector}
          assigneeSelectorMenuPosition={assigneeSelectorMenuPosition}
          selectedAnnotation={selectedAnnotation}
          onAssigneeChanged={(assignee) => uiState.onAssigneeChanged(assignee)}
          pdfUrl={pdfUrl}
          includeFabricCanvas
          callbacks={{
            onLoaded: this.onPdfLoaded,
            onPageChanging: this.onPageChanging,
            onPageChanged: this.onPageChanged
          }}
        />
        {errors && <FormError message={errors.originalFile} />}
        <DocumentAssignmentRules assignmentRules={companyDocument.assignmentRules}/>
        <ModalButtons
          onSave={this.save}
          onCancel={() => uiState.cancel()}
        />
      </div>
    );
  }
}

async function load({pdfUrl, companyDocument, onSave, onCancel, includeTaxFields}) {
  const {annotations, assignmentRules} = companyDocument;
  const module = await import('fabric');
  const uiState = new AnnotatablePdfPreviewState({
    companyDocument, pdfUrl, store: annotatablePdfStore, onSave, onCancel, annotations, assignmentRules, includeTaxFields, fabric: module.fabric
  });
  await uiState.load();
  return {uiState};
}

export default loader(observer(AnnotatablePdfPreview), load);
